/**
 * FTUE first-milestone marker (JestSDK.markFirstMilestone).
 *
 * Jest uses markFirstMilestone() as a standardized cross-game read on FTUE
 * performance. FantasyPRIME has no substantial tutorial, so the earliest
 * meaningful milestone is the player's first real contest action:
 *
 *   - saving picks in a main-event undercard, or
 *   - entering a prop-boost event with a confirmed roster.
 *
 * Call sites invoke this after one of those actions succeeds; everything else
 * lives here: the SDK method is feature-detected (older jestsdk.js builds
 * don't expose it), the call is deduped per player via localStorage, and
 * failures are swallowed — milestone telemetry must never break the action
 * that triggered it.
 */

const DEDUPE_PREFIX = 'fantasyprime:first-milestone:';

// localStorage is only written after the SDK call resolves, so overlapping
// fire-and-forget calls could both pass hasMarked() — this tracks keys with a
// call in flight to close that window (failed calls are removed so a later
// action can retry).
const inFlight = new Set<string>();

export async function markFirstMilestoneOnce(
  sdk: JestSDKApi | null | undefined,
): Promise<void> {
  if (!sdk || typeof sdk.markFirstMilestone !== 'function') return;
  try {
    // keyed per player, not per device — a second player on the same device
    // still gets their own milestone reported
    const { playerId } = await sdk.getPlayer();
    const key = `${DEDUPE_PREFIX}${playerId}`;
    if (hasMarked(key) || inFlight.has(key)) return;
    inFlight.add(key);
    try {
      await sdk.markFirstMilestone();
      remember(key);
    } finally {
      inFlight.delete(key);
    }
  } catch (err) {
    console.error('[jest-client] markFirstMilestone failed:', err);
  }
}

function hasMarked(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function remember(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // non-critical: the milestone was still reported, only local dedupe failed
  }
}
